import {
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Box,
  Divider,
  Chip,
  InputAdornment,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  PersonOutline as PersonOutlineIcon,
  LocalOffer as LocalOfferIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
  Add as AddIcon,
} from "@mui/icons-material";

import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import WSTextField from "../../Components/TextBox/WSTextBox";
import { FormProvider, useForm } from "react-hook-form";
import WSPhone from "../../Components/Phone/WSPhone";
import { WSTabs } from "../../Components/Tabs/WSTabs";
import { GstAddress } from "./GstAddress";
import { CreditBalance } from "./CreditBalance";
import { AdditionalFields } from "./AdditionalFields";
import WSButton from "../../Components/Button/WSButton";
import { callAPI, Method, dateFormat } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { useDispatch } from "react-redux";
import { resetDataComponent } from "../../StateManagement/actions";

export const Party: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id;

  const theme = useTheme();
  const { showMessage } = useSnackbar();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [loadingSaveAndNew, setLoadingSaveAndNew] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const methods = useForm<any>({
    defaultValues: {
      partyName: "",
      gstIn: "",
      phoneNumber: "",
      gstType: "0",
      state: "0",
      email: "",
      billingAddress: "",
      shippingAddress: "",
      openingBalance: 0,
      asOfDate: dateFormat(new Date(), "DD-MM-YYYY"),
      creditLimit: false,
      enableDrugLicenceNumber: false,
      showPrintDrugLicenceNumber: false,
      drugLicenceNumber: "",
      enablePanNumber: false,
      showPrintPanNumber: false,
      panNumber: "",
      enableAgeOrGender: false,
      showPrintAgeOrGender: false,
      ageOrGender: "",
      enableDrugLicenceExpDate: false,
      showPrintDrugLicenceExpDate: false,
      drugLicenceExpDate: dateFormat(new Date(), "DD-MM-YYYY"),
    },
  });

  const { handleSubmit, reset, setValue } = methods;

  // Load data for edit
  useEffect(() => {
    console.log("🎯 useEffect triggered with id:", id);
    if (id) {
      setIsEditMode(true);
      loadPartyData(id);
    }
  }, [id]); // ✅ Only depends on id


  const loadPartyData = async (partyId: string) => {
    try {
      console.log("🔍 Starting loadPartyData for partyId:", partyId);

      // ✅ Make API call
      const response = await callAPI(`Vyapar/Party/${partyId}`, Method.GET);

      // ✅ COMPREHENSIVE DEBUGGING
      console.log("📡 COMPLETE response object:", response);
      console.log("📡 response.data:", response.data);
      console.log("📡 response keys:", Object.keys(response || {}));

      // ✅ Handle different API response structures
      let partyData = null;
      if (response.data) {
        partyData = response.data;
      } else if (response.result) {
        partyData = response.result;
      } else if (response.party) {
        partyData = response.party;
      } else {
        partyData = response;
      }

      console.log("📊 Final partyData:", partyData);

      if (partyData && Object.keys(partyData).length > 0) {
        console.log("✅ Data found, preparing form values...");

        const formData = {
          partyName: partyData.partyName || partyData.party_name || "",
          gstIn: partyData.gstin || partyData.gst_in || "",
          phoneNumber: partyData.phoneNumber || partyData.phone_number || "",
          gstType: partyData.gsttype || partyData.gst_type || "0",
          state: partyData.state || "0",
          email: partyData.email || "",
          billingAddress: partyData.billingAddress || partyData.billing_address || "",
          shippingAddress: partyData.shippingAddress || partyData.shipping_address || "",
          openingBalance: Number(partyData.openingBalance) || 0,
          asOfDate: partyData.asOfDate
            ? dateFormat(new Date(partyData.asOfDate), "DD-MM-YYYY")
            : dateFormat(new Date(), "DD-MM-YYYY"),
          creditLimit: Boolean(partyData.creditLimit),
          enableDrugLicenceNumber: Boolean(partyData.enableDrugLicenceNumber),
          showPrintDrugLicenceNumber: Boolean(partyData.showPrintDrugLicenceNumber),
          drugLicenceNumber: partyData.drugLicenceNumber || "",
          enablePanNumber: Boolean(partyData.enablePanNumber),
          showPrintPanNumber: Boolean(partyData.showPrintPanNumber),
          panNumber: partyData.panNumber || "",
          enableAgeOrGender: Boolean(partyData.enableAgeOrGender),
          showPrintAgeOrGender: Boolean(partyData.showPrintAgeOrGender),
          ageOrGender: partyData.ageOrGender || "",
          enableDrugLicenceExpDate: Boolean(partyData.enableDrugLicenceExpDate),
          showPrintDrugLicenceExpDate: Boolean(partyData.showPrintDrugLicenceExpDate),
          drugLicenceExpDate: partyData.drugLicenceExpDate
            ? dateFormat(new Date(partyData.drugLicenceExpDate), "DD-MM-YYYY")
            : dateFormat(new Date(), "DD-MM-YYYY"),
        };

        console.log("🔄 About to reset form with:", formData);

        // ✅ Reset form
        reset(formData);

        console.log("✅ Form reset complete. Current form values:", methods.getValues());

      } else {
        console.log("❌ No valid data found in response");
        showMessage("No party data found", "warning");
      }
    } catch (error: any) {
      console.error("💥 FULL API ERROR:", error);
      showMessage("Failed to fetch party data: " + (error.message || "Unknown error"), "error");
    }
  };


  const tabsData = [
    { label: "GST & Address", content: <GstAddress /> },
    { label: "Credit & Balance", content: <CreditBalance /> },
    { label: "Additional Fields", content: <AdditionalFields /> },
  ];

  const handleDataSaveAndNew = useCallback(async (data: any) => {
    try {
      setLoadingSaveAndNew(true);
      const request = prepareRequestData(data);
      await callAPI("Vyapar/Party", Method.POST, request);
      reset();
      showMessage("Party created successfully", "success");
    } catch (error) {
      showMessage("Failed to create party", "error");
    } finally {
      setLoadingSaveAndNew(false);
    }
  }, [reset, showMessage]);

  const handleData = useCallback(async (data: any) => {
    try {
      setLoading(true);
      const request = prepareRequestData(data);

      let response;
      if (isEditMode && id) {
        response = await callAPI(`Vyapar/Party/${id}`, Method.PUT, request);
      } else {
        response = await callAPI("Vyapar/Party", Method.POST, request);
      }

      dispatch(resetDataComponent("partyGrid"));
      showMessage(response?.message || (isEditMode ? "Party updated successfully" : "Party created successfully"), "success");

      // ✅ ALWAYS navigate to list after save/update
      navigate("/party/list");

    } catch (error) {
      showMessage("Failed to save party", "error");
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode, dispatch, showMessage, navigate, resetDataComponent]);


  const prepareRequestData = (data: any) => ({
    partyName: data.partyName || "",
    gstin: data.gstIn || "",
    phoneNumber: data.phoneNumber || "",
    billingAddress: data.billingAddress || "",
    shippingAddress: data.shippingAddress || "",
    gsttype: data.gstType || "",
    state: data.state || "",
    email: data.email || "",
    openingBalance: Number(data.openingBalance) || 0,
    asOfDate: data.asOfDate ? new Date(data.asOfDate).toISOString() : new Date().toISOString(),
    creditLimit: data.creditLimit || false,
    enableDrugLicenceNumber: data.enableDrugLicenceNumber || false,
    showPrintDrugLicenceNumber: data.showPrintDrugLicenceNumber || false,
    drugLicenceNumber: data.drugLicenceNumber || "",
    enablePanNumber: data.enablePanNumber || false,
    showPrintPanNumber: data.showPrintPanNumber || false,
    panNumber: data.panNumber || "",
    enableAgeOrGender: data.enableAgeOrGender || false,
    showPrintAgeOrGender: data.showPrintAgeOrGender || false,
    ageOrGender: data.ageOrGender || "",
    enableDrugLicenceExpDate: data.enableDrugLicenceExpDate || false,
    showPrintDrugLicenceExpDate: data.showPrintDrugLicenceExpDate || false,
    drugLicenceExpDate: data.drugLicenceExpDate ? new Date(data.drugLicenceExpDate).toISOString() : new Date().toISOString(),
  });

  const handleClose = () => navigate("/party/list");

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleData)}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          }}
        >
          {/* Card Header with Gradient Background */}
          <Box
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              px: 3,
              py: 2,
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      width: 40,
                      height: 40,
                      boxShadow: `0 4px 10px ${alpha(theme.palette.primary.main, 0.3)}`,
                    }}
                  >
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        letterSpacing: '-0.5px',
                      }}
                    >
                      {isEditMode ? "Edit vendor" : "Add New vendor"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isEditMode ? "Update party information" : "Enter party details to create a new record"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item>
                <Tooltip title="Close">
                  <IconButton
                    onClick={handleClose}
                    sx={{
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        color: theme.palette.error.main,
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>

          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Party Name */}
              <Grid item xs={12} sm={4}>
                <Box sx={{ position: 'relative' }}>
                  <WSTextField
                    label="vendor Name"
                    {...methods.register("partyName")}
                    id="partyName"
                    name="partyName"
                    control={methods.control}
                    rules={{ required: "vendor Name is required" }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.background.paper, 0.8),
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                        },
                      },
                    }}
                    suffix={<PersonOutlineIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />}
                  />
                </Box>
              </Grid>

              {/* GSTIN */}
              <Grid item xs={12} sm={4}>
                <WSTextField
                  label="GSTIN"
                  {...methods.register("gstIn")}
                  id="gstIn"
                  name="gstIn"
                  control={methods.control}
                  
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.background.paper, 0.8),
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    },
                  }}
                  suffix={<LocalOfferIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />}
                />
              </Grid>

              {/* Phone Number */}
              <Grid item xs={12} sm={4}>
                <WSPhone
                  label="Phone Number"
                  id="phoneNumber"
                  name="phoneNumber"
                  control={methods.control}
                  rules={{ required: "Phone Number is required" }}
                />
              </Grid>

              {/* Tabs Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Chip
                    label="Additional Details"
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                      px: 1,
                    }}
                  />
                </Divider>

                <WSTabs
                  id="partTab"
                  setValue={setValue}
                  control={methods.control}
                  name="partTab"
                  tabs={tabsData}
                  justifyTabs="flex-start"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Form Actions */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 3,
            p: 2,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Tooltip title="Save and add another party">
            <WSButton
              id="saveAndPrintBtn"
              variant="outlined"
              color="secondary"
              size="medium"
              label="Save & New"
              onClick={handleSubmit(handleDataSaveAndNew)}
              loading={loadingSaveAndNew}
              startIcon={<AddIcon />}
            />
          </Tooltip>

          <Tooltip title="Save ">
            <WSButton
              id="saveBtn"
              variant="contained"
              color="primary"
              size="medium"
              label="Save vendor"
              type="submit"
              startIcon={<SaveIcon />}
              loading={loading}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease',
              }}
            />
          </Tooltip>
        </Box>
      </form>
    </FormProvider>
  );
});
